import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "layouts/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import QuickActionsPanel from "components/ui/QuickActionsPanel";
import { useRole } from "context/RoleContext";
import { ROLES, ROLE_PROFILES } from "config/roleConfig";
import { ROUTES } from "config/navConfig";
import { MOCK_FUNDS, MOCK_SPVS } from "data/mockData";
import MetricsGrid from "./components/MetricsGrid";
import MilestonesPanel from "./components/MilestonesPanel";
import RecentDocuments from "./components/RecentDocuments";
import ESGAlertsPanel from "./components/ESGAlertsPanel";
import PortfolioChart from "./components/PortfolioChart";
import ActiveSPVsTable from "./components/ActiveSPVsTable";
import ExportReportModal from "./components/ExportReportModal";
import PortfolioIntelligenceCards from "./components/PortfolioIntelligenceCards";

const IC_PENDING_COUNT = 1;

const fmtM = (v) => v != null ? `£${(v / 1_000_000).toFixed(0)}m` : '—';

const LP_STATUS = {
  'closed':        { label: 'Closed',        color: '#059669', bg: 'rgba(5,150,105,0.10)'  },
  'soft-circled':  { label: 'Soft-circled',  color: '#d97706', bg: 'rgba(217,119,6,0.10)'  },
};

const FUND_STATUS = {
  'Closed':  { color: '#059669', bg: 'rgba(5,150,105,0.08)'  },
  'Raising': { color: '#2563eb', bg: 'rgba(37,99,235,0.08)'  },
};

const FUND_PIPELINE = {
  'fund-2': { label: '1 deal at IC Review', dealName: 'Tyne Bridge Refinancing', route: ROUTES.pipeline },
};

export default function PortfolioDashboard() {
  const navigate = useNavigate();
  const { role, activeContext } = useRole();

  const MARKETPLACE_ACCESS = [
    ROLES.INVESTMENT_TEAM, ROLES.EXTERNAL_INVESTOR,
    ROLES.CO_INVESTOR, ROLES.DEAL_ORIGINATOR, ROLES.PLATFORM_ADMIN,
  ];
  const canAccessMarketplace = MARKETPLACE_ACCESS.includes(role);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleExportClick = () => {
    setExportModalOpen(true);
  };

  return (
    <AppLayout>
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border border-border bg-card z-50 max-w-sm"
          style={{ zIndex: "var(--z-toast)" }}
        >
          <Icon
            name={toast?.type === "success" ? "CheckCircle" : "AlertCircle"}
            size={18}
            color={toast?.type === "success" ? "var(--color-success)" : "var(--color-warning)"}
          />
          <span className="font-caption text-foreground text-sm">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ zIndex: "var(--z-nav)" }}
        >
          <div className="pl-10 lg:pl-0">
            <h1
              className="font-heading font-semibold text-foreground"
              style={{ fontSize: "1.375rem", lineHeight: 1.3 }}
            >
              Portfolio Dashboard
            </h1>
            <p className="font-caption text-muted-foreground text-sm mt-0.5">
              {activeContext?.entity || 'Greenfield Renewables Fund I'} · {ROLE_PROFILES[role]?.label || 'Investment Team'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <QuickActionsPanel
              context="portfolio-dashboard"
              userRole={role}
              layout="horizontal"
            />
            <Button
              variant="outline"
              size="sm"
              iconName="FileDown"
              iconPosition="left"
              iconSize={15}
              onClick={handleExportClick}
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* Role-variant context strips */}
        {role === ROLES.INVESTMENT_TEAM && (
          <div className="flex items-center justify-between gap-3 px-6 md:px-10 lg:px-12 py-3 border-b border-border" style={{ background: 'rgba(217,119,6,0.04)' }}>
            <div className="flex items-center gap-2.5">
              <Icon name="ClipboardCheck" size={15} color="#d97706" />
              <span className="font-caption text-sm text-foreground">
                <span className="font-semibold">1 deal at IC Review</span> — Tyne Bridge Refinancing (Fund II) — IC meeting 13 April.
              </span>
            </div>
            <button onClick={() => navigate(ROUTES.icSubmission)} className="font-caption text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 text-white" style={{ background: '#d97706' }}>
              View Submission
            </button>
          </div>
        )}
        {role === ROLES.IC_MEMBER && (
          <div className="flex items-center justify-between gap-3 px-6 md:px-10 lg:px-12 py-3 border-b border-border" style={{ background: 'rgba(99,102,241,0.04)' }}>
            <div className="flex items-center gap-2.5">
              <Icon name="ClipboardList" size={15} color="#6366f1" />
              <span className="font-caption text-sm text-foreground">
                <span className="font-semibold">{IC_PENDING_COUNT} submission</span> pending your vote.
              </span>
            </div>
            <button onClick={() => navigate(ROUTES.icSubmission)} className="font-caption text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 text-white" style={{ background: '#6366f1' }}>
              Review Now
            </button>
          </div>
        )}
        {role === ROLES.ASSET_MGMT_TEAM && (
          <div className="flex items-center justify-between gap-3 px-6 md:px-10 lg:px-12 py-3 border-b border-border" style={{ background: 'rgba(8,145,178,0.04)' }}>
            <div className="flex items-center gap-2.5">
              <Icon name="BarChart2" size={15} color="#0891b2" />
              <span className="font-caption text-sm text-foreground">
                <span className="font-semibold">2 assets</span> due for Q1 review.
              </span>
            </div>
            <button onClick={() => navigate(ROUTES.operationsConsole)} className="font-caption text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 text-white" style={{ background: '#0891b2' }}>
              Open Operations Console
            </button>
          </div>
        )}
        {role === ROLES.FINANCE_TEAM && (
          <div className="flex items-center justify-between gap-3 px-6 md:px-10 lg:px-12 py-3 border-b border-border" style={{ background: 'rgba(5,150,105,0.04)' }}>
            <div className="flex items-center gap-2.5">
              <Icon name="TrendingUp" size={15} color="#059669" />
              <span className="font-caption text-sm text-foreground">
                <span className="font-semibold">Q1 2026 valuations</span> are due for submission.
              </span>
            </div>
            <button onClick={() => navigate(ROUTES.valuationView)} className="font-caption text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 text-white" style={{ background: '#059669' }}>
              Open Valuation View
            </button>
          </div>
        )}
        {role === ROLES.LEGAL_COMP_TEAM && (
          <div className="flex items-center justify-between gap-3 px-6 md:px-10 lg:px-12 py-3 border-b border-border" style={{ background: 'rgba(217,119,6,0.04)' }}>
            <div className="flex items-center gap-2.5">
              <Icon name="ShieldAlert" size={15} color="#d97706" />
              <span className="font-caption text-sm text-foreground">
                <span className="font-semibold">3 counterparties</span> have pending KYC/AML reviews.
              </span>
            </div>
            <button onClick={() => navigate(ROUTES.counterparties)} className="font-caption text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 text-white" style={{ background: '#d97706' }}>
              Review Counterparties
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">

          {/* Fund Structure Cards */}
          <section aria-label="Fund structure">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              {MOCK_FUNDS.map((fund) => {
                const statusCfg = FUND_STATUS[fund.status] ?? { color: 'var(--color-muted-foreground)', bg: 'rgba(0,0,0,0.05)' };
                const pipeline = FUND_PIPELINE[fund.id];
                const raisedAmount = fund.status === 'Closed' ? fund.closedSize : fund.raisedSoftCircled;
                const raisedLabel = fund.status === 'Closed' ? 'Closed' : 'Soft-circled';
                const raisedPct = raisedAmount != null ? Math.min(100, Math.round((raisedAmount / fund.targetSize) * 100)) : 0;
                const spvList = MOCK_SPVS.filter((s) => fund.spvIds.includes(s.id));
                return (
                  <div key={fund.id} className="card" style={{ padding: '24px 28px' }}>
                    {/* Fund header */}
                    <div className="flex items-start justify-between mb-1 gap-3">
                      <div>
                        <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.0625rem', lineHeight: 1.3 }}>
                          {fund.name}
                        </h2>
                        <p className="font-caption text-muted-foreground mt-0.5" style={{ fontSize: '0.8rem' }}>
                          Vintage {fund.vintage} · Target {fmtM(fund.targetSize)}
                        </p>
                      </div>
                      <span
                        className="font-caption text-xs font-semibold px-2.5 py-1 shrink-0"
                        style={{ color: statusCfg.color, background: statusCfg.bg, borderRadius: '4px' }}
                      >
                        {fund.status}
                      </span>
                    </div>

                    {/* Raised bar */}
                    <div className="mt-3 mb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-caption text-xs text-muted-foreground">{raisedLabel}</span>
                        <span className="font-caption text-xs font-semibold text-foreground">{fmtM(raisedAmount)} of {fmtM(fund.targetSize)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-border overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${raisedPct}%`, background: statusCfg.color, transition: 'width 0.4s ease' }}
                        />
                      </div>
                    </div>

                    {/* LP Commitments */}
                    <div className="mb-4">
                      <p className="font-caption font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontSize: '0.7rem' }}>
                        LP Commitments
                      </p>
                      <div>
                        {fund.lpCommitments.map((lp, i) => {
                          const lpCfg = LP_STATUS[lp.status] ?? { label: lp.status, color: 'var(--color-muted-foreground)', bg: 'rgba(0,0,0,0.05)' };
                          return (
                            <div
                              key={lp.id}
                              className="flex items-center justify-between py-2"
                              style={{ borderTop: i === 0 ? '1px solid var(--color-border)' : undefined, borderBottom: '1px solid var(--color-border)' }}
                            >
                              <div className="min-w-0 mr-3">
                                <span className="font-caption text-sm text-foreground truncate block">{lp.name}</span>
                                <span className="font-caption text-muted-foreground" style={{ fontSize: '0.72rem' }}>{lp.type}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="font-caption text-sm font-semibold text-foreground">{fmtM(lp.commitmentAmount)}</span>
                                <span
                                  className="font-caption font-semibold"
                                  style={{ fontSize: '0.68rem', color: lpCfg.color, background: lpCfg.bg, padding: '2px 7px', borderRadius: '3px' }}
                                >
                                  {lpCfg.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Portfolio Holdings + pipeline */}
                    <div className="pt-3 border-t border-border">
                      <p className="font-caption font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontSize: '0.7rem' }}>
                        Portfolio Holdings
                      </p>
                      {spvList.map((spv) => (
                        <div key={spv.id} className="flex items-center justify-between py-1">
                          <div>
                            <p className="font-caption text-sm text-foreground font-medium">
                              {spv.assetSnapshot?.name ?? spv.name}
                            </p>
                            {spv.assetSnapshot && (
                              <p className="font-caption text-muted-foreground" style={{ fontSize: '0.72rem' }}>
                                {spv.name} · {spv.assetSnapshot.sector}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => navigate(ROUTES.spvDashboard, { state: { spvId: spv.id } })}
                            className="font-caption text-xs font-semibold hover:underline shrink-0"
                            style={{ color: 'var(--color-primary)' }}
                          >
                            Open Dashboard →
                          </button>
                        </div>
                      ))}
                      {pipeline && (
                        <div
                          className="flex items-center justify-between mt-3 pt-2.5 border-t border-border"
                        >
                          <div className="flex items-center gap-2">
                            <Icon name="Clock" size={13} color="#d97706" />
                            <span className="font-caption text-xs text-foreground">
                              <span className="font-semibold" style={{ color: '#d97706' }}>{pipeline.label}</span>
                              {' — '}{pipeline.dealName}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate(pipeline.route)}
                            className="font-caption text-xs font-semibold hover:underline"
                            style={{ color: 'var(--color-primary)' }}
                          >
                            View Pipeline →
                          </button>
                        </div>
                      )}
                    </div>

                    {/* View Fund link */}
                    <div className="mt-4 pt-3 border-t border-border flex justify-end">
                      <button
                        onClick={() => navigate(ROUTES.fundDetail.replace(':id', fund.id))}
                        className="font-caption text-xs font-semibold flex items-center gap-1 hover:underline"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        View Fund →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Metrics Grid */}
          <section aria-label="Portfolio metrics">
            <MetricsGrid />
          </section>

          {/* Portfolio Intelligence Cards */}
          <section aria-label="Portfolio intelligence">
            <PortfolioIntelligenceCards />
          </section>

          {/* Portfolio Chart */}
          <section aria-label="Portfolio performance chart">
            <PortfolioChart />
          </section>

          {/* Milestones + Recent Documents */}
          <section aria-label="Milestones and documents" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <MilestonesPanel />
            <RecentDocuments />
          </section>

          {/* ESG Alerts */}
          <section aria-label="ESG alerts and compliance">
            <ESGAlertsPanel />
          </section>

          {/* Active SPVs Table */}
          <section aria-label="Active SPVs and deals">
            <ActiveSPVsTable />
          </section>

          {/* Bottom Quick Nav — visible to marketplace-access roles only */}
          {canAccessMarketplace && <section
            aria-label="Quick navigation"
            className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-6"
          >
            {[
              {
                label: "Browse Opportunities",
                icon: "TrendingUp",
                route: ROUTES.opportunities,
                desc: "6 live deals",
                color: "var(--color-primary)",
                bg: "rgba(59,76,76,0.06)",
              },
              {
                label: "Create SPV",
                icon: "Plus",
                route: ROUTES.spvCreation,
                desc: "Launch new vehicle",
                color: "var(--color-primary)",
                bg: "rgba(59,76,76,0.1)",
              },
              {
                label: "Investor Onboarding",
                icon: "UserCheck",
                route: ROUTES.investorOnboarding,
                desc: "KYC & accreditation",
                color: "var(--color-success)",
                bg: "rgba(5,150,105,0.06)",
              },
              {
                label: "SPV Dashboard",
                icon: "Briefcase",
                route: ROUTES.spvDashboard,
                desc: "Manage deals",
                color: "var(--color-secondary)",
                bg: "rgba(74,107,138,0.06)",
              },
            ]?.map((item) => (
              <button
                key={item?.route}
                onClick={() => navigate(item?.route)}
                className="card text-left hover-lift press-effect transition-all duration-250 focus:outline-none"
                style={{ padding: "16px 20px" }}
              >
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg mb-3"
                  style={{ background: item?.bg }}
                >
                  <Icon name={item?.icon} size={20} color={item?.color} />
                </div>
                <p className="font-caption font-semibold text-foreground text-sm">{item?.label}</p>
                <p className="font-caption text-muted-foreground mt-0.5" style={{ fontSize: "0.75rem" }}>
                  {item?.desc}
                </p>
              </button>
            ))}
          </section>}
        </div>
      </div>
      {/* Export Modal */}
      <ExportReportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />
      {/* Mobile FAB */}
      <QuickActionsPanel
        context="portfolio-dashboard"
        userRole={role}
        layout="floating"
      />
    </AppLayout>
  );
}